import {
    Directive,
    ElementRef,
    NgZone,
    AfterViewInit,
    Input,
    Renderer2,
    Inject,
    ViewContainerRef,
    PLATFORM_ID,
    ChangeDetectorRef,
    Optional,
    HostListener,
    inject
} from '@angular/core';

import { Tooltip, TooltipStyle } from 'primeng/tooltip';
import _ from 'underscore';
import { DomHandler } from 'primeng/dom';
import { Subscription } from 'rxjs';

import { ActionKeys } from '../../Forms/enums/keys.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { PrimeNG } from 'primeng/config';
import { Nullable } from 'primeng/ts-helpers';
import { TooltipRegistryService } from '../service/tooltip-registry.service';


@Directive({
    selector: '[agprTooltip]',
    providers: [TooltipStyle],
    standalone: false
})
export class TooltipDirective extends Tooltip implements AfterViewInit {

    /* eslint-disable @angular-eslint/no-input-rename */
    /**
     * ### Accessibility Usage
     * This is meant to replace pTooltip because pTooltip only shows up when hovered OR focused, while we want it to show up in both cases.
     * In cases where this directive cannot be used, use pTooltip + attr.aria-label
     * + ***useTooltipAsAriaLabel*** - by default the tooltip content is the same as the aria-label. Set false if you want screen reader users
     *                                 to hear a different label
     * + ***initiallyFocusable*** - Use this to control an HTML element's initial focusability. By default the elenment is focusable
     * + ***interactiveTooltip*** - Use this if the tooltip has interactive elements like a link in it
     */
    @Input('agprTooltip') set textContent(text: string) {
        this.content = this.localizationService.transform(text);
        this.setOption({ tooltipLabel: this.content });
        if (this.useTooltipAsAriaLabel) {
            this.el.nativeElement.setAttribute('aria-label', this.content);
        }
    }
    @Input('useTooltipAsAriaLabel') useTooltipAsAriaLabel: boolean = true;
    @Input('initiallyFocusable') initiallyFocusable: boolean = true;
    @Input('forceTooltipOnFocus') forceTooltipOnFocus: boolean = false;
    @Input('interactiveTooltip') interactiveTooltip: boolean = false;
    @Input('tooltipAllowsHtml') tooltipAllowsHtml: boolean = false;
    @Input('tooltipClickListener') tooltipClickListener?: Nullable<Function>;

    debouncedMouseLeave: any;

    keyboardListener: Function;
    clickListener: (event: MouseEvent) => void;

    subscriptions: Subscription[] = [];
    destroyed: boolean = false;

    private tooltipRegistry = inject(TooltipRegistryService);

    constructor(
        public el: ElementRef,
        public zone: NgZone,
        public config: PrimeNG,
        public localizationService: LocalizationService,
        public renderer2: Renderer2,
        protected viewContainerRef: ViewContainerRef,
        public cd: ChangeDetectorRef,
        @Inject(PLATFORM_ID) platformId: any,
        @Optional() protected userPrefsService: UserPreferencesService
    ) {
        super(zone, viewContainerRef);
        this.tooltipRegistry.register(this);
        this.config.zIndex.tooltip = 1000000 + DomHandler.zindex;
        if (this.userPrefsService) {
            this.subscriptions.push(
                this.userPrefsService?.getUserAppPreferences().subscribe(prefs => {
                    this.tooltipEvent = prefs.generalPrefs.accessibility.tooltipsOnFocus ? null : 'hover';
                })
            );
        }

    }

    ngAfterViewInit(): void {
        this.zone.runOutsideAngular(() => {
            switch (this.tooltipEvent) {
                case 'hover':
                    this.initHoverForTooltipEvent();
                    if (this.forceTooltipOnFocus) {
                        this.initFocusForTooltipEvent();
                    }
                    this.initKeyboardForTooltipEvent();
                    break;
                case 'focus':
                    this.initFocusForTooltipEvent();
                    this.initKeyboardForTooltipEvent();
                    break;
                default:
                    this.initHoverForTooltipEvent();
                    this.initFocusForTooltipEvent();
                    this.initKeyboardForTooltipEvent();
            }
        });

        if (this.useTooltipAsAriaLabel) {
            // Strip any html elements from the text, otherwise SR will announce them
            const div = document.createElement('div');
            div.innerHTML = this.content as string;
            const textOnly = div.innerText;
            this.el.nativeElement.setAttribute('aria-label', textOnly);
        }

        if (!!this.content) {
            this.el.nativeElement.setAttribute('tabindex', this.initiallyFocusable ? '0' : '-1');
        }
    }

    create(): void {
        super.create();
        this.container.style.zIndex = String(1000000 + DomHandler.zindex);
        this.container.setAttribute('aria-role', 'tooltip');
        if (this.tooltipAllowsHtml) {
            this.container.innerHTML = this.content as string;
            this.container.style.backgroundColor = 'var(--agpr-white)';
            this.container.style.color = 'var(--agpr-text-primary)';
            this.container.style.padding = '10px 15px';
            this.container.style.lineHeight = '1.7';
            this.container.style.borderRadius = '4px';
            this.container.style.maxWidth = 'none';
            this.container.style.boxShadow = '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)';
        }
        if (this.interactiveTooltip) {
            this.container.style.pointerEvents = 'auto';
            this.container.addEventListener('mouseleave', this.mouseLeaveListener);
            this.container.addEventListener('mouseenter', this.tooltipMouseEnter.bind(this));
        }

        if(this.tooltipClickListener) {
            this.clickListener = (event: MouseEvent) => {
                event.preventDefault();
                if (this.tooltipClickListener) {
                    this.tooltipClickListener(event);
                }
            };
            this.container.addEventListener('click', this.clickListener);
        }
    }

    show(): void {
        if (this.destroyed || !this.el.nativeElement.isConnected) {
            return;
        }
        super.show();
    }

    onKeyUp(event: KeyboardEvent): void {
        if (event.key === ActionKeys.ESCAPE) {
            this.deactivate();
        }
    }

    private tooltipMouseEnter(e: Event) {
        this.debouncedMouseLeave.cancel();
    }

    private initHoverForTooltipEvent(): void {
        this.mouseEnterListener = this.onMouseEnter.bind(this);
        this.mouseLeaveListener = this.onMouseLeave.bind(this);
        if (this.interactiveTooltip) {
            this.debouncedMouseLeave = _.debounce(this.mouseLeaveListener, 500);
            this.el.nativeElement.addEventListener('mouseleave', this.debouncedMouseLeave);
        } else {
            this.el.nativeElement.addEventListener('mouseleave', this.mouseLeaveListener);
        }
        this.el.nativeElement.addEventListener('mouseenter', this.mouseEnterListener);
    }

    private initFocusForTooltipEvent(): void {
        this.focusListener = this.onFocus.bind(this);
        this.blurListener = this.onBlur.bind(this);
        this.el.nativeElement.addEventListener('focus', this.focusListener);
        this.el.nativeElement.addEventListener('blur', this.blurListener);
    }

    private initKeyboardForTooltipEvent(): void {
        this.keyboardListener = this.onKeyUp.bind(this);
        this.el.nativeElement.addEventListener('keyup', this.keyboardListener);
    }

    private removeHostListeners(): void {
        const host = this.el.nativeElement;
        host.removeEventListener('mouseenter', this.mouseEnterListener);
        host.removeEventListener('mouseleave', this.debouncedMouseLeave ?? this.mouseLeaveListener);
        host.removeEventListener('focus', this.focusListener);
        host.removeEventListener('blur', this.blurListener);
        host.removeEventListener('keyup', this.keyboardListener);
    }

    onDestroy(): void {
        this.destroyed = true;
        this.tooltipRegistry.unregister(this);
        this.removeHostListeners();
        this.debouncedMouseLeave?.cancel();
        if (this.clickListener) {
            this.container?.removeEventListener('click', this.clickListener);
        }
        this.subscriptions.forEach(sub => sub.unsubscribe());
        super.onDestroy();
    }
}
